import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  BellRing,
  CheckCircle2,
  Clock3,
  Eye,
  Globe2,
  Info,
  Loader2,
  Mail,
  MapPin,
  MessageSquareText,
  Search,
  Send,
  Smartphone,
  UsersRound,
  X
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { adminAPI } from '../../services/api';
import { ADMIN_PERMISSIONS } from '../../constants';
import { useAuthStore } from '../../store';
import { hasPermission } from '../../utils/rbac';

const tabs = [
  { id: 'compose', label: 'Send' },
  { id: 'campaigns', label: 'History' },
  { id: 'delivery', label: 'Delivery Health' }
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const channelOptions = {
  candidates: [
    { value: 'candidate_in_app', label: 'Candidate app + push', icon: BellRing },
    { value: 'candidate_whatsapp', label: 'WhatsApp', icon: Smartphone }
  ],
  employers: [
    { value: 'employer_email', label: 'Email', icon: Mail }
  ]
};

const defaultForm = {
  audience: 'candidates',
  channels: ['candidate_in_app'],
  recipientScope: 'individual',
  campaignName: '',
  title: '',
  subject: '',
  message: '',
  scheduledFor: '',
  expiresAt: '',
  acknowledgeBulkSend: false
};

const defaultRecipientFilters = {
  search: '',
  status: 'all',
  region: '',
  country: '',
  visibilityStatus: 'all',
  availabilityStatus: 'all',
  verificationStatus: 'all',
  industry: '',
  hasPhone: ''
};

const campaignFiltersDefault = {
  status: 'all',
  channel: 'all',
  search: '',
  page: 1,
  limit: 25
};

const extractError = (error, fallback) => (
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');

const formatDateTime = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const labelize = (value) => String(value || 'not_set')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const channelLabel = (channel) => ({
  candidate_in_app: 'Candidate app + push',
  candidate_whatsapp: 'Candidate WhatsApp',
  employer_email: 'Employer email'
}[channel] || labelize(channel));

const statusClass = (status) => ({
  sent: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  completed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  queued: 'border-amber-200 bg-amber-50 text-amber-700',
  processing: 'border-slate-300 bg-slate-50 text-slate-700',
  failed: 'border-rose-200 bg-rose-50 text-rose-700',
  skipped: 'border-slate-200 bg-slate-50 text-slate-600',
  cancelled: 'border-slate-300 bg-slate-100 text-slate-600'
}[status] || 'border-slate-200 bg-slate-50 text-slate-600');

const SkeletonBlock = ({ className = '' }) => (
  <div className={`animate-pulse bg-slate-200/80 ${className}`} />
);

const Chip = ({ children, className = '' }) => (
  <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium ${className}`}>
    {children}
  </span>
);

const Panel = ({ title, subtitle, icon: Icon, children, actions }) => (
  <section className="rounded-[24px] border border-slate-100 bg-white shadow-sm flex flex-col overflow-hidden">
    <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-start sm:justify-between bg-white">
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-blue-100/50 bg-blue-50 text-blue-600">
            <Icon size={20} />
          </span>
        )}
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm leading-5 text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
    {children}
  </section>
);

const EmptyState = ({ children }) => (
  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-5 py-8 text-center text-sm leading-6 text-slate-500">
    {children}
  </div>
);

const PaginationControls = ({ pagination, onPageChange, onLimitChange, pageSizeOptions = PAGE_SIZE_OPTIONS }) => {
  const page = Number(pagination?.page || 1);
  const limit = Number(pagination?.limit || pageSizeOptions[0]);
  const total = Number(pagination?.total || 0);
  const totalPages = Math.max(1, Number(pagination?.totalPages || 1));
  const start = total ? ((page - 1) * limit) + 1 : 0;
  const end = total ? Math.min(page * limit, total) : 0;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
      <div>
        Showing <span className="font-semibold text-slate-900">{start}-{end}</span> of <span className="font-semibold text-slate-900">{total}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={limit}
          onChange={(event) => onLimitChange(Number(event.target.value))}
          className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
        >
          {pageSizeOptions.map((option) => <option key={option} value={option}>{option} rows</option>)}
        </select>
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Previous
        </button>
        <span className="px-2 text-xs font-medium uppercase tracking-[0.12em] text-slate-500">{page} / {totalPages}</span>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

const Modal = ({ title, children, onClose, width = 'max-w-5xl' }) => (
  <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/50 px-4 py-8">
    <section className={`w-full ${width} rounded-[24px] bg-white shadow-2xl`}>
      <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50"
        >
          <X size={17} />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </section>
  </div>
);

const NotificationsSkeleton = () => (
  <div className="min-h-full px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8" aria-label="Loading notifications">
    <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
      <div className="space-y-3">
        <SkeletonBlock className="h-4 w-44 rounded-md" />
        <SkeletonBlock className="h-10 w-72 rounded-lg" />
        <SkeletonBlock className="h-4 w-[44rem] max-w-full rounded-md" />
      </div>
      <SkeletonBlock className="h-10 w-28 rounded-lg" />
    </div>
    <div className="mb-5 flex gap-2">
      {tabs.map((tab) => <SkeletonBlock key={tab.id} className="h-10 w-40 rounded-lg" />)}
    </div>
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => <SkeletonBlock key={index} className="h-28 rounded-lg" />)}
    </div>
    <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
      <SkeletonBlock className="h-[560px] rounded-lg" />
      <SkeletonBlock className="h-[560px] rounded-lg" />
    </div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, detail }) => (
  <article className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-6">
      <span className="text-sm font-semibold text-slate-600">{label}</span>
      {Icon && (
        <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100/50 shrink-0">
          <Icon className="w-5 h-5" />
        </div>
      )}
    </div>
    <div>
      <p className="text-3xl font-bold tracking-tight text-slate-900">{formatNumber(value)}</p>
      {detail && <p className="mt-2 text-xs font-medium text-slate-400">{detail}</p>}
    </div>
  </article>
);

const fieldClass = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100';

const cleanFilters = (filters) => Object.fromEntries(
  Object.entries(filters).filter(([, value]) => value !== '' && value !== 'all' && value !== null && value !== undefined)
);

const RecipientSearch = ({
  form,
  recipientFilters,
  setRecipientFilters,
  selectedRecipients,
  setSelectedRecipients
}) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const channelKey = form.channels.join('|');

  useEffect(() => {
    setPage(1);
  }, [form.audience, channelKey]);

  const searchQuery = useQuery(
    ['notification-recipient-search', form.audience, form.channels, recipientFilters, page, limit],
    () => adminAPI.searchNotificationRecipients({
      audience: form.audience,
      channels: form.channels.join(','),
      limit,
      page,
      ...cleanFilters(recipientFilters)
    }),
    { keepPreviousData: true, staleTime: 10000 }
  );

  const recipients = searchQuery.data?.recipients || [];
  const pagination = searchQuery.data?.pagination || {
    page,
    limit,
    total: recipients.length,
    totalPages: 1
  };
  const total = pagination.total || 0;
  const selectedIds = Object.keys(selectedRecipients).map(Number);

  const updateFilter = (key, value) => {
    setPage(1);
    setRecipientFilters((current) => ({ ...current, [key]: value }));
  };

  const updateLimit = (nextLimit) => {
    setPage(1);
    setLimit(nextLimit);
  };

  const toggleRecipient = (recipient) => {
    setSelectedRecipients((current) => {
      const next = { ...current };
      if (next[recipient.userId]) delete next[recipient.userId];
      else next[recipient.userId] = recipient;
      return next;
    });
  };

  const clearSelected = () => setSelectedRecipients({});

  return (
    <Panel
      title="People"
      subtitle="Pick specific people or use filters. Leave filters empty in bulk mode to send to everyone in the selected audience."
      actions={selectedIds.length ? (
        <button
          type="button"
          onClick={clearSelected}
          className="text-sm font-semibold text-slate-500 transition hover:text-slate-900"
        >
          Clear selected
        </button>
      ) : null}
    >
      <div className="space-y-4 p-4">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_150px_150px_150px]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={17} />
            <input
              value={recipientFilters.search}
              onChange={(event) => updateFilter('search', event.target.value)}
              className={`${fieldClass} pl-10`}
              placeholder={form.audience === 'candidates' ? 'Search candidate name, email, phone' : 'Search company, employer, email'}
            />
          </label>
          <select
            value={recipientFilters.status}
            onChange={(event) => updateFilter('status', event.target.value)}
            className={fieldClass}
          >
            <option value="all">Any user status</option>
            <option value="active">Active</option>
            <option value="pending_verification">Pending verification</option>
            <option value="suspended">Suspended</option>
          </select>
          <label className="relative block">
            <MapPin className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              value={recipientFilters.region}
              onChange={(event) => updateFilter('region', event.target.value)}
              className={`${fieldClass} pl-9`}
              placeholder="Region or city"
            />
          </label>
          <label className="relative block">
            <Globe2 className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              value={recipientFilters.country}
              onChange={(event) => updateFilter('country', event.target.value)}
              className={`${fieldClass} pl-9`}
              placeholder="Country"
            />
          </label>
        </div>

        {form.audience === 'candidates' ? (
          <div className="grid gap-3 md:grid-cols-3">
            <select
              value={recipientFilters.visibilityStatus}
              onChange={(event) => updateFilter('visibilityStatus', event.target.value)}
              className={fieldClass}
            >
              <option value="all">Any visibility</option>
              <option value="live">Live</option>
              <option value="hidden">Hidden</option>
              <option value="paused">Paused</option>
            </select>
            <select
              value={recipientFilters.availabilityStatus}
              onChange={(event) => updateFilter('availabilityStatus', event.target.value)}
              className={fieldClass}
            >
              <option value="all">Any availability</option>
              <option value="available">Available</option>
              <option value="open_to_opportunities">Open to opportunities</option>
              <option value="not_looking">Not looking</option>
            </select>
            <select
              value={recipientFilters.hasPhone}
              onChange={(event) => updateFilter('hasPhone', event.target.value)}
              className={fieldClass}
            >
              <option value="">Any phone state</option>
              <option value="true">Has phone</option>
              <option value="false">Missing phone</option>
            </select>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            <select
              value={recipientFilters.verificationStatus}
              onChange={(event) => updateFilter('verificationStatus', event.target.value)}
              className={fieldClass}
            >
              <option value="all">Any verification</option>
              <option value="verified">Verified</option>
              <option value="under_review">Under review</option>
              <option value="pending_documents">Pending documents</option>
              <option value="rejected">Rejected</option>
              <option value="suspended">Suspended</option>
            </select>
            <input
              value={recipientFilters.industry}
              onChange={(event) => updateFilter('industry', event.target.value)}
              className={fieldClass}
              placeholder="Industry"
            />
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <span>
            <span className="font-semibold">{formatNumber(total)}</span> people match the current filters.
            {form.recipientScope === 'individual' && selectedIds.length > 0 && (
              <span> {formatNumber(selectedIds.length)} selected for this campaign.</span>
            )}
          </span>
          {searchQuery.isFetching && <Loader2 size={16} className="animate-spin" />}
        </div>

        {searchQuery.isError ? (
          <EmptyState>{extractError(searchQuery.error, 'Recipient search failed.')}</EmptyState>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <div className="max-h-[460px] overflow-auto">
              <table className="min-w-[960px] divide-y divide-slate-100 text-left text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50/50 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4">Select</th>
                    <th className="px-6 py-4">Recipient</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Segment</th>
                    <th className="px-6 py-4">Readiness</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {recipients.map((recipient) => {
                    const selected = Boolean(selectedRecipients[recipient.userId]);
                    const whatsappReady = recipient.readiness?.candidateWhatsApp;
                    const pushReady = recipient.readiness?.candidatePush;

                    return (
                      <tr key={recipient.userId} className={selected ? 'bg-slate-50' : 'hover:bg-slate-50'}>
                        <td className="px-6 py-4">
                          {form.recipientScope === 'individual' ? (
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleRecipient(recipient)}
                              className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                            />
                          ) : (
                            <span className="text-xs font-medium text-slate-500">Bulk</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <p className="max-w-[320px] truncate font-medium text-slate-900">{recipient.companyName || recipient.displayName}</p>
                          {recipient.companyName && <p className="mt-1 max-w-[320px] truncate text-xs text-slate-500">{recipient.displayName}</p>}
                          <p className="mt-1 max-w-[320px] truncate text-xs text-slate-500">{recipient.email || recipient.phone || 'No contact value'}</p>
                        </td>
                        <td className="px-6 py-4"><Chip className={statusClass(recipient.userStatus)}>{labelize(recipient.userStatus)}</Chip></td>
                        <td className="px-6 py-4 text-slate-600">
                          <p>{recipient.location || recipient.region || 'No location'}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {recipient.visibilityStatus ? labelize(recipient.visibilityStatus) : recipient.verificationStatus ? labelize(recipient.verificationStatus) : recipient.industry || 'No segment'}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          {form.audience === 'candidates' ? (
                            <div className="flex flex-wrap gap-2">
                              <Chip className={pushReady ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-500'}>
                                Push {pushReady ? 'ready' : 'no token'}
                              </Chip>
                              <Chip className={whatsappReady ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}>
                                WhatsApp {whatsappReady ? 'ready' : recipient.whatsappNotificationsEnabled === false ? 'off' : 'not ready'}
                              </Chip>
                            </div>
                          ) : (
                            <Chip className={recipient.email ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}>
                              Email {recipient.email ? 'ready' : 'missing'}
                            </Chip>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {!recipients.length && (
                    <tr>
                      <td colSpan={5} className="p-4">
                        <EmptyState>No recipients match the current filters.</EmptyState>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationControls
              pagination={pagination}
              onPageChange={(nextPage) => setPage(Math.min(Math.max(1, nextPage), Math.max(1, pagination.totalPages || 1)))}
              onLimitChange={updateLimit}
            />
          </div>
        )}
      </div>
    </Panel>
  );
};

const CampaignDetailModal = ({ campaignId, onClose }) => {
  const [filters, setFilters] = useState({ status: 'all', channel: 'all', search: '', page: 1, limit: 50 });
  const detailQuery = useQuery(
    ['notification-campaign-detail', campaignId, filters],
    () => adminAPI.getNotificationCampaign(campaignId, cleanFilters(filters)),
    { enabled: Boolean(campaignId) }
  );

  const campaign = detailQuery.data?.campaign;
  const recipients = detailQuery.data?.recipients || [];
  const pagination = detailQuery.data?.pagination || {
    page: filters.page,
    limit: filters.limit,
    total: recipients.length,
    totalPages: 1
  };

  const updateFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      page: key === 'page' ? value : 1
    }));
  };

  const updateLimit = (nextLimit) => {
    setFilters((current) => ({ ...current, limit: nextLimit, page: 1 }));
  };

  return (
    <Modal title="Notification campaign detail" onClose={onClose} width="max-w-6xl">
      {detailQuery.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, index) => <SkeletonBlock key={index} className="h-20 rounded-lg" />)}
        </div>
      ) : detailQuery.isError ? (
        <EmptyState>{extractError(detailQuery.error, 'Campaign detail could not be loaded.')}</EmptyState>
      ) : (
        <div className="space-y-5">
          <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">{campaign?.campaignName}</h3>
                <p className="mt-1 text-sm text-slate-500">{campaign?.subject || campaign?.title || 'No subject/title'}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Chip className={statusClass(campaign?.status)}>{labelize(campaign?.status)}</Chip>
                  {(campaign?.channels || []).map((item) => <Chip key={item} className="border-slate-200 bg-white text-slate-700">{channelLabel(item)}</Chip>)}
                  <Chip className="border-slate-200 bg-white text-slate-600">{formatNumber(campaign?.uniqueRecipientCount)} unique recipients</Chip>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-5">
                <div><p className="text-slate-500">Rows</p><p className="font-semibold text-slate-900">{formatNumber(campaign?.recipientCount)}</p></div>
                <div><p className="text-slate-500">Queued</p><p className="font-semibold text-slate-900">{formatNumber(campaign?.queuedCount)}</p></div>
                <div><p className="text-slate-500">Sent</p><p className="font-semibold text-slate-900">{formatNumber(campaign?.deliveredCount)}</p></div>
                <div><p className="text-slate-500">Failed</p><p className="font-semibold text-slate-900">{formatNumber(campaign?.failedCount)}</p></div>
                <div><p className="text-slate-500">Skipped</p><p className="font-semibold text-slate-900">{formatNumber(campaign?.skippedCount)}</p></div>
              </div>
            </div>
          </section>

          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_170px_170px]">
            <input
	              value={filters.search}
	              onChange={(event) => updateFilter('search', event.target.value)}
	              className={fieldClass}
	              placeholder="Search recipient name, company, email, phone"
	            />
	            <select
	              value={filters.status}
	              onChange={(event) => updateFilter('status', event.target.value)}
	              className={fieldClass}
            >
              <option value="all">Any status</option>
              <option value="queued">Queued</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="skipped">Skipped</option>
            </select>
	            <select
	              value={filters.channel}
	              onChange={(event) => updateFilter('channel', event.target.value)}
	              className={fieldClass}
            >
              <option value="all">Any channel</option>
              <option value="candidate_in_app">Candidate app + push</option>
              <option value="candidate_whatsapp">Candidate WhatsApp</option>
              <option value="employer_email">Employer email</option>
            </select>
          </div>

	          <div className="overflow-hidden rounded-lg border border-slate-200">
	            <div className="overflow-x-auto">
	            <table className="min-w-[980px] divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4">Recipient</th>
                  <th className="px-6 py-4">Channel</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Job</th>
                  <th className="px-6 py-4">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recipients.map((recipient) => (
                  <tr key={recipient.id}>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-900">{recipient.companyName || recipient.displayName}</p>
                      <p className="mt-1 text-xs text-slate-500">{recipient.email || recipient.phone || 'No contact value'}</p>
                      {recipient.lastError && <p className="mt-2 max-w-xl text-xs leading-5 text-rose-600">{recipient.lastError}</p>}
                    </td>
                    <td className="px-6 py-4 text-slate-600">{channelLabel(recipient.channel)}</td>
                    <td className="px-6 py-4"><Chip className={statusClass(recipient.status)}>{labelize(recipient.status)}</Chip></td>
                    <td className="px-6 py-4 text-xs text-slate-500">
                      {recipient.emailJobId ? `Email job ${recipient.emailJobId} (${recipient.emailJobStatus || 'queued'})` : null}
                      {recipient.whatsappJobId ? `WhatsApp job ${recipient.whatsappJobId} (${recipient.whatsappJobStatus || 'queued'})` : null}
                      {!recipient.emailJobId && !recipient.whatsappJobId ? `Notification ${recipient.notificationId || 'not created'}` : null}
                    </td>
                    <td className="px-6 py-4 text-slate-500">{formatDateTime(recipient.sentAt || recipient.failedAt || recipient.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
	            </table>
	            {!recipients.length && <div className="p-4"><EmptyState>No recipients match the selected delivery filters.</EmptyState></div>}
	            </div>
	            <PaginationControls
	              pagination={pagination}
	              onPageChange={(nextPage) => updateFilter('page', Math.min(Math.max(1, nextPage), Math.max(1, pagination.totalPages || 1)))}
	              onLimitChange={updateLimit}
	              pageSizeOptions={[25, 50, 100, 200]}
	            />
	          </div>
        </div>
      )}
    </Modal>
  );
};

function NotificationsPage() {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((state) => state.user);
  const canManage = hasPermission(currentUser, ADMIN_PERMISSIONS.NOTIFICATIONS_MANAGE);
  const [activeTab, setActiveTab] = useState(canManage ? 'compose' : 'campaigns');
  const [form, setForm] = useState(defaultForm);
  const [recipientFilters, setRecipientFilters] = useState(defaultRecipientFilters);
  const [selectedRecipients, setSelectedRecipients] = useState({});
  const [campaignFilters, setCampaignFilters] = useState(campaignFiltersDefault);
  const [detailCampaignId, setDetailCampaignId] = useState(null);

  const notificationsQuery = useQuery(
    ['admin-notifications', campaignFilters],
    () => adminAPI.getNotifications(cleanFilters(campaignFilters)),
    { staleTime: 15000 }
  );

  const createMutation = useMutation(
    (payload) => adminAPI.createNotificationCampaign(payload),
    {
      onSuccess: (response) => {
        toast.success(response?.message || 'Notification campaign queued.');
        setSelectedRecipients({});
        setForm((current) => ({
          ...defaultForm,
          audience: current.audience,
          channels: current.audience === 'candidates' ? ['candidate_in_app'] : ['employer_email']
        }));
        queryClient.invalidateQueries('admin-notifications');
      },
      onError: (error) => {
        toast.error(extractError(error, 'Notification campaign could not be created.'));
      }
    }
  );

  const data = notificationsQuery.data || {};
  const summary = data.summary || {};
  const queueHealth = data.queueHealth || {};
  const campaigns = data.campaigns || [];
  const recentRecipients = data.recentRecipients || [];
  const selectedIds = useMemo(() => Object.keys(selectedRecipients).map(Number), [selectedRecipients]);
  const campaignPagination = data.pagination || {
    page: campaignFilters.page,
    limit: campaignFilters.limit,
    total: campaigns.length,
    totalPages: 1
  };

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateCampaignFilter = (key, value) => {
    setCampaignFilters((current) => ({
      ...current,
      [key]: value,
      page: key === 'page' ? value : 1
    }));
  };

  const updateCampaignLimit = (nextLimit) => {
    setCampaignFilters((current) => ({ ...current, limit: nextLimit, page: 1 }));
  };

  const setAudience = (audience) => {
    setForm((current) => ({
      ...current,
      audience,
      channels: audience === 'candidates' ? ['candidate_in_app'] : ['employer_email'],
      subject: audience === 'employers' ? current.subject : '',
      title: audience === 'candidates' ? current.title : '',
      recipientScope: 'individual',
      acknowledgeBulkSend: false
    }));
    setRecipientFilters(defaultRecipientFilters);
    setSelectedRecipients({});
  };

  const toggleChannel = (channel) => {
    setForm((current) => {
      if (current.audience === 'employers') return { ...current, channels: ['employer_email'] };
      const exists = current.channels.includes(channel);
      const nextChannels = exists
        ? current.channels.filter((item) => item !== channel)
        : [...current.channels, channel];
      return {
        ...current,
        channels: nextChannels.length ? nextChannels : [channel]
      };
    });
  };

  const submitCampaign = (event) => {
    event.preventDefault();
    if (createMutation.isLoading) return;

    const payload = {
      ...form,
      recipientUserIds: form.recipientScope === 'individual' ? selectedIds : [],
      filters: form.recipientScope === 'bulk_filter' ? cleanFilters(recipientFilters) : {},
      acknowledgeBulkSend: form.recipientScope === 'bulk_filter' ? form.acknowledgeBulkSend : false
    };

    if (form.recipientScope === 'individual' && !selectedIds.length) {
      toast.error('Select at least one recipient or switch to bulk filter mode.');
      return;
    }

    createMutation.mutate(payload);
  };

  if (notificationsQuery.isLoading && !notificationsQuery.data) return <NotificationsSkeleton />;

  return (
    <div className="min-h-full px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <PageHeader
        title="Communications"
        description="Send candidate app and WhatsApp messages, or employer emails, to everyone, filtered groups, or selected people."
      />

      {notificationsQuery.isError && (
        <div className="mb-5 rounded-[24px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700">
          {extractError(notificationsQuery.error, 'Unable to load notification data.')}
        </div>
      )}

      {!canManage && (
        <div className="mb-5 rounded-[24px] border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-700 shadow-sm">
          You have view-only access. Creating notification campaigns requires <span className="font-semibold">notifications.manage</span>.
        </div>
      )}

      <section className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={MessageSquareText} label="Campaigns" value={summary.totalCampaigns} detail={`${formatNumber(summary.campaigns30d)} created in the last 30 days`} />
        <StatCard icon={UsersRound} label="Delivery rows" value={summary.deliveryRows} detail="One row per person and channel" />
        <StatCard icon={CheckCircle2} label="Sent" value={summary.deliveredRows} detail="App notifications created, emails sent, or WhatsApp delivered" />
        <StatCard icon={AlertTriangle} label="Failed or skipped" value={(summary.failedRows || 0) + (summary.skippedRows || 0)} detail={`${formatNumber(summary.failedRows)} failed, ${formatNumber(summary.skippedRows)} skipped`} />
      </section>

      <nav className="mb-5 flex gap-2 overflow-x-auto">
        {tabs.filter((tab) => canManage || tab.id !== 'compose').map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
              activeTab === tab.id
                ? 'bg-slate-950 text-white shadow-sm'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'compose' && canManage && (
        <form onSubmit={submitCampaign} className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_430px]">
          <div className="space-y-5">
            <Panel title="Message" subtitle="Start with who should receive it, then write the message." icon={Send}>
              <div className="space-y-4 p-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">Send to</span>
                    <select
                      value={form.audience}
                      onChange={(event) => setAudience(event.target.value)}
                      className={fieldClass}
                    >
                      <option value="candidates">Candidates</option>
                      <option value="employers">Employers</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">People</span>
                    <select
                      value={form.recipientScope}
                      onChange={(event) => updateForm('recipientScope', event.target.value)}
                      className={fieldClass}
                    >
                      <option value="individual">Specific people</option>
                      <option value="bulk_filter">Everyone matching filters</option>
                    </select>
                  </label>
                </div>

                <div>
                  <span className="mb-2 block text-sm font-semibold text-slate-700">Channels</span>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {channelOptions[form.audience].map((option) => {
                      const Icon = option.icon;
                      const checked = form.channels.includes(option.value);
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => toggleChannel(option.value)}
                          className={`flex items-center gap-3 rounded-[24px] border px-4 py-3 text-left transition ${
                            checked ? 'border-slate-950 bg-slate-50 text-slate-900' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          <Icon size={18} />
                          <span className="font-semibold">{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">Campaign name</span>
                    <input
                      value={form.campaignName}
                      onChange={(event) => updateForm('campaignName', event.target.value)}
                      className={fieldClass}
                      placeholder="July candidate update"
                    />
                  </label>
                  {form.channels.includes('employer_email') ? (
                    <label className="block">
                      <span className="mb-1.5 block text-sm font-semibold text-slate-700">Email subject</span>
                      <input
                        value={form.subject}
                        onChange={(event) => updateForm('subject', event.target.value)}
                        className={fieldClass}
                        placeholder="Important Sanspath update"
                      />
                    </label>
                  ) : form.channels.includes('candidate_in_app') ? (
                    <label className="block">
                      <span className="mb-1.5 block text-sm font-semibold text-slate-700">In-app title</span>
                      <input
                        value={form.title}
                        onChange={(event) => updateForm('title', event.target.value)}
                        className={fieldClass}
                        placeholder="Quick update"
                      />
                    </label>
                  ) : (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm leading-6 text-slate-600">
                      WhatsApp-only sends use the message body. Keep it short and direct.
                    </div>
                  )}
                </div>

                <label className="block">
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700">Message</span>
                  <textarea
                    value={form.message}
                    onChange={(event) => updateForm('message', event.target.value)}
                    className={`${fieldClass} min-h-[180px] resize-y leading-6`}
                    placeholder="Write the message. You can use {{first_name}}, {{name}}, {{company_name}}, {{email}}, and {{location}}."
                  />
                </label>

                <div className="grid gap-3 md:grid-cols-2">
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">Schedule</span>
                    <input
                      type="datetime-local"
                      value={form.scheduledFor}
                      onChange={(event) => updateForm('scheduledFor', event.target.value)}
                      className={fieldClass}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">Expiry</span>
                    <input
                      type="datetime-local"
                      value={form.expiresAt}
                      onChange={(event) => updateForm('expiresAt', event.target.value)}
                      className={fieldClass}
                    />
                  </label>
                </div>

                {form.recipientScope === 'bulk_filter' && (
                  <label className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-800">
                    <input
                      type="checkbox"
                      checked={form.acknowledgeBulkSend}
                      onChange={(event) => updateForm('acknowledgeBulkSend', event.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-amber-300 text-slate-900 focus:ring-slate-500"
                    />
                    <span>
                      I confirm this should be queued for everyone matching the current filters.
                    </span>
                  </label>
                )}

                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
                  <Info size={16} className="mr-2 inline text-slate-900" />
                  Candidate app messages are also sent as push where the candidate has an active device token. WhatsApp is only queued for candidates who have not opted out. Employer emails are queued and retried by the backend.
                </div>
              </div>
            </Panel>

            <RecipientSearch
              form={form}
              recipientFilters={recipientFilters}
              setRecipientFilters={setRecipientFilters}
              selectedRecipients={selectedRecipients}
              setSelectedRecipients={setSelectedRecipients}
            />
          </div>

          <aside className="space-y-5">
            <Panel title="Review" subtitle="Confirm the audience and channel before sending." icon={CheckCircle2}>
              <div className="space-y-4 p-4 text-sm">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Audience</p>
                  <p className="mt-1 font-semibold text-slate-900">{labelize(form.audience)}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {form.channels.map((channel) => <Chip key={channel} className="border-slate-200 bg-white text-slate-700">{channelLabel(channel)}</Chip>)}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-xs text-slate-500">People</p>
                    <p className="mt-1 font-semibold text-slate-900">{form.recipientScope === 'individual' ? 'Specific' : 'All matching'}</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-xs text-slate-500">Count</p>
                    <p className="mt-1 font-semibold text-slate-900">{form.recipientScope === 'individual' ? formatNumber(selectedIds.length) : 'All matching'}</p>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs text-slate-500">Schedule</p>
                  <p className="mt-1 font-semibold text-slate-900">{form.scheduledFor ? formatDateTime(form.scheduledFor) : 'Immediately'}</p>
                </div>

                <button
                  type="submit"
                  disabled={createMutation.isLoading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {createMutation.isLoading ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                  Send
                </button>
              </div>
            </Panel>

            <Panel title="WhatsApp Sender" subtitle="Bulk WhatsApp requires a connected sender session." icon={Smartphone}>
              <div className="space-y-3 p-4 text-sm">
                <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5">
                  <span className="text-slate-500">Status</span>
                  <Chip className={queueHealth.whatsappStatus?.connected ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}>
                    {queueHealth.whatsappStatus?.connected ? 'Connected' : labelize(queueHealth.whatsappStatus?.status || 'not connected')}
                  </Chip>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5">
                  <span className="text-slate-500">Pending jobs</span>
                  <span className="font-semibold text-slate-900">{formatNumber(queueHealth.whatsappPending)}</span>
                </div>
                <div className="text-xs leading-5 text-slate-500">
                  <p>Connection and QR management are handled on the WhatsApp sender setup page.</p>
                  <Link to="/communications/whatsapp" className="mt-2 inline-flex font-semibold text-slate-900 hover:text-cyan-700">
                    Open WhatsApp sender setup
                  </Link>
                </div>
              </div>
            </Panel>
          </aside>
        </form>
      )}

      {activeTab === 'campaigns' && (
        <div className="space-y-5">
          <Panel
            title="History"
            subtitle="Search previous sends and open delivery details for each person."
            icon={MessageSquareText}
          >
	            <div className="space-y-4 p-4">
	              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_160px_180px_140px]">
	                <input
	                  value={campaignFilters.search}
	                  onChange={(event) => updateCampaignFilter('search', event.target.value)}
	                  className={fieldClass}
	                  placeholder="Search campaign name, title, subject"
	                />
	                <select
	                  value={campaignFilters.status}
	                  onChange={(event) => updateCampaignFilter('status', event.target.value)}
	                  className={fieldClass}
                >
                  <option value="all">Any status</option>
                  <option value="queued">Queued</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
	                <select
	                  value={campaignFilters.channel}
	                  onChange={(event) => updateCampaignFilter('channel', event.target.value)}
	                  className={fieldClass}
                >
                  <option value="all">Any channel</option>
                  <option value="candidate_in_app">Candidate app + push</option>
                  <option value="candidate_whatsapp">Candidate WhatsApp</option>
	                  <option value="employer_email">Employer email</option>
	                </select>
	                <select
	                  value={campaignFilters.limit}
	                  onChange={(event) => updateCampaignLimit(Number(event.target.value))}
	                  className={fieldClass}
	                >
	                  {PAGE_SIZE_OPTIONS.map((option) => <option key={option} value={option}>{option} rows</option>)}
	                </select>
	              </div>

	              <div className="overflow-hidden rounded-lg border border-slate-200">
	                <div className="overflow-x-auto">
	                  <table className="min-w-[1120px] divide-y divide-slate-100 text-left text-sm">
	                    <thead className="bg-slate-50 text-xs font-medium uppercase tracking-[0.12em] text-slate-500">
	                      <tr>
	                        <th className="px-6 py-4">Campaign</th>
	                        <th className="px-6 py-4">Status</th>
	                        <th className="px-6 py-4">Audience</th>
	                        <th className="px-6 py-4">Recipients</th>
	                        <th className="px-6 py-4">Delivery</th>
	                        <th className="px-6 py-4">Created</th>
	                        <th className="px-6 py-4 text-right">Actions</th>
	                      </tr>
	                    </thead>
	                    <tbody className="divide-y divide-slate-100 bg-white">
	                      {campaigns.map((campaign) => (
	                        <tr key={campaign.id} className="hover:bg-slate-50">
	                          <td className="px-4 py-4">
	                            <p className="max-w-[320px] truncate font-medium text-slate-900">{campaign.campaignName}</p>
	                            <p className="mt-1 max-w-[320px] truncate text-xs text-slate-500">{campaign.subject || campaign.title || campaign.message}</p>
	                          </td>
	                          <td className="px-4 py-4"><Chip className={statusClass(campaign.status)}>{labelize(campaign.status)}</Chip></td>
	                          <td className="px-4 py-4">
	                            <p className="font-medium text-slate-800">{labelize(campaign.audience)}</p>
	                            <div className="mt-2 flex flex-wrap gap-1.5">
	                              {(campaign.channels || []).map((channel) => <Chip key={channel} className="border-slate-200 bg-white text-slate-700">{channelLabel(channel)}</Chip>)}
	                            </div>
	                          </td>
	                          <td className="px-4 py-4">
	                            <p className="font-semibold text-slate-900">{formatNumber(campaign.uniqueRecipientCount)}</p>
	                            <p className="mt-1 text-xs text-slate-500">{formatNumber(campaign.recipientCount)} delivery rows</p>
	                          </td>
	                          <td className="px-4 py-4 text-slate-600">
	                            <p>{formatNumber(campaign.deliveredCount)} sent</p>
	                            <p className="mt-1 text-xs text-slate-500">{formatNumber(campaign.failedCount)} failed, {formatNumber(campaign.skippedCount)} skipped</p>
	                          </td>
	                          <td className="px-4 py-4 text-slate-600">{formatDateTime(campaign.createdAt)}</td>
	                          <td className="px-4 py-4 text-right">
	                            <button
	                              type="button"
	                              onClick={() => setDetailCampaignId(campaign.id)}
	                              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
	                            >
	                              <Eye size={15} />
	                              Details
	                            </button>
	                          </td>
	                        </tr>
	                      ))}
	                      {!campaigns.length && (
	                        <tr>
	                          <td colSpan={7} className="p-5">
	                            <EmptyState>No notification campaigns match the selected filters.</EmptyState>
	                          </td>
	                        </tr>
	                      )}
	                    </tbody>
	                  </table>
	                </div>
	                <PaginationControls
	                  pagination={campaignPagination}
	                  onPageChange={(nextPage) => updateCampaignFilter('page', Math.min(Math.max(1, nextPage), Math.max(1, campaignPagination.totalPages || 1)))}
	                  onLimitChange={updateCampaignLimit}
	                />
	              </div>
	            </div>
          </Panel>
        </div>
      )}

      {activeTab === 'delivery' && (
        <div className="grid gap-5 xl:grid-cols-[420px_minmax(0,1fr)]">
          <Panel title="Queue Health" subtitle="Current delivery queue state for admin notification channels." icon={Clock3}>
            <div className="space-y-3 p-4 text-sm">
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">WhatsApp</span>
                  <Chip className={queueHealth.whatsappStatus?.connected ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}>
                    {queueHealth.whatsappStatus?.connected ? 'Connected' : labelize(queueHealth.whatsappStatus?.status || 'not connected')}
                  </Chip>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-500">Pending</p><p className="font-semibold text-slate-900">{formatNumber(queueHealth.whatsappPending)}</p></div>
                  <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-500">Processing</p><p className="font-semibold text-slate-900">{formatNumber(queueHealth.whatsappProcessing)}</p></div>
                  <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-500">Failed</p><p className="font-semibold text-slate-900">{formatNumber(queueHealth.whatsappFailed)}</p></div>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">Employer email</span>
                  <Mail size={16} className="text-slate-900" />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                  <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-500">Pending</p><p className="font-semibold text-slate-900">{formatNumber(queueHealth.employerEmailPending)}</p></div>
                  <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-500">Failed</p><p className="font-semibold text-slate-900">{formatNumber(queueHealth.employerEmailFailed)}</p></div>
                </div>
              </div>
            </div>
          </Panel>

          <Panel title="Recent Deliveries" subtitle="Most recent recipient delivery rows across campaigns." icon={BellRing}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4">Recipient</th>
                    <th className="px-6 py-4">Channel</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentRecipients.map((recipient) => (
                    <tr key={recipient.id}>
                      <td className="px-6 py-4">
                        <p className="font-semibold text-slate-900">{recipient.companyName || recipient.displayName}</p>
                        <p className="mt-1 text-xs text-slate-500">{recipient.email || recipient.phone}</p>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{channelLabel(recipient.channel)}</td>
                      <td className="px-6 py-4"><Chip className={statusClass(recipient.status)}>{labelize(recipient.status)}</Chip></td>
                      <td className="px-6 py-4 text-slate-500">{formatDateTime(recipient.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!recentRecipients.length && <div className="p-5"><EmptyState>No delivery rows have been created.</EmptyState></div>}
            </div>
          </Panel>
        </div>
      )}

      {detailCampaignId && (
        <CampaignDetailModal
          campaignId={detailCampaignId}
          onClose={() => setDetailCampaignId(null)}
        />
      )}
    </div>
  );
}

export default NotificationsPage;
